<template>
  <AppModal
    :model-value="modelValue"
    :aria-label="displayTitle"
    :aria-describedby="messageId"
    size="sm"
    :mobile-sheet="false"
    :show-close="false"
    @update:model-value="emit('update:modelValue', $event)"
    @close="emit('cancel')"
  >
    <!-- Content -->
    <div class="pt-2">
      <div style="border-top: 2px solid var(--text-primary); width: 24px; margin-bottom: 12px;"></div>
      <h3 :id="titleId" class="mb-2" style="font-size: var(--text-xl); color: var(--text-primary);">
        {{ displayTitle }}
      </h3>
      <p :id="messageId" style="color: var(--text-secondary); font-size: var(--text-sm);">
        {{ displayMessage }}
      </p>
    </div>

    <!-- The way out that isn't destructive, when one exists. Full width and
         above the row because it is the recommended answer, not a third
         equal option — three buttons abreast is unreadable on a phone. -->
    <AppButton v-if="altText" block class="mt-4" @click="handleAlt">
      {{ altText }}
    </AppButton>

    <!-- Actions -->
    <div class="flex gap-3 pt-4 pb-2">
      <AppButton ref="cancelBtn" variant="ghost" class="flex-1" @click="handleCancel">
        {{ cancelText || t("common.cancel") }}
      </AppButton>
      <AppButton
        :variant="variant === 'danger' ? 'danger' : variant === 'warning' ? 'warning' : 'primary'"
        class="flex-1"
        @click="handleConfirm"
      >
        {{ confirmText || t("common.confirm_action") }}
      </AppButton>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { hapticWarning } from "../services/hapticsService"
// Must be a VALUE import, not `type AppButton`: a type-only import type-checks
// and builds fine but erases at runtime, so <AppButton> failed to resolve and
// this dialog rendered with NO buttons — every confirmation in the app (delete
// a transaction, "add anyway" on a duplicate, delete a saved preset) became a
// prompt you could not answer. cancelBtn below is typed structurally rather
// than as InstanceType<typeof AppButton> so the binding never looks type-only;
// otherwise Biome's useImportType pre-commit fix rewrites it back to `type` and
// silently breaks every dialog again. Same trap as the quick-add inputs in
// OverviewView — this is its second occurrence.
import { AppButton, AppModal } from "./ui"

/**
 * Confirm/cancel prompt — a thin preset over the AppModal primitive, which
 * owns teleporting, stacking, focus and scroll-lock behavior. Text props
 * fall back to translated defaults, so callers that omit them stay localized.
 */
interface Props {
  modelValue: boolean
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  /**
   * Optional non-destructive alternative, e.g. "Create an account" on a
   * confirm that would otherwise throw the user's data away. Omit and the
   * dialog stays a plain two-button confirm. Emits `alt`.
   */
  altText?: string
  variant?: "danger" | "warning" | "info"
}

const props = withDefaults(defineProps<Props>(), {
  variant: "danger",
})

const { t } = useI18n()
const displayTitle = computed(() => props.title || t("common.confirm_action"))
const displayMessage = computed(() => props.message || t("common.confirm"))

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void
  (e: "confirm"): void
  (e: "cancel"): void
  (e: "alt"): void
}>()

const uid = Math.random().toString(36).slice(2, 8)
const titleId = `confirm-title-${uid}`
const messageId = `confirm-msg-${uid}`
const cancelBtn = ref<{ focus?: () => void } | null>(null)

function handleConfirm() {
  emit("confirm")
  emit("update:modelValue", false)
}

// Deliberately not `cancel`: the caller usually wants to open something else,
// and `cancel` also fires on Escape / backdrop, which must stay a plain
// dismissal rather than launching a flow the user did not ask for.
function handleAlt() {
  emit("alt")
  emit("update:modelValue", false)
}

function handleCancel() {
  emit("cancel")
  emit("update:modelValue", false)
}

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      // Destructive/warning prompts announce themselves with a tactile nudge
      if (props.variant !== "info") hapticWarning()
      // Default focus to cancel (safer for destructive dialogs)
      nextTick(() => cancelBtn.value?.focus?.())
    }
  },
)
</script>
